import React, { useState, useRef, useEffect } from 'react';
import {
  Menu,
  Search,
  Bell,
  Settings,
  Moon,
  Sun,
  User,
  Users,
  HelpCircle,
  X,
  ChevronDown,
  LogOut,
  Shield,
  Globe,
  Building2,
  ShieldCheck,
  Scale,
  CalendarDays,
  FileSpreadsheet,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useData } from '../../contexts/DataContext';
import { useFramework } from '../../contexts/FrameworkContext';
import { useOrganization } from '../../contexts/OrganizationContext';
import { HelpModal } from '../Help/HelpModal';
import { SettingsModal } from '../Settings/SettingsModal';
import { UserManagementModal } from '../Users/UserManagementModal';
import { SearchDropdown } from '../Search/SearchDropdown';
import { isViewEnabled } from '../../config/features';
import { CalendarPanel } from '../Calendar/CalendarPanel';
import { AIHeaderButton } from '../AI/AIHeaderButton';
import { GlobalBulkTaskUploadModal } from '../Controls/GlobalBulkTaskUploadModal';

// Tipo de vistas disponibles en la aplicación
type ViewType = 'dashboard' | 'national-ra' | 'network-ra' | 'audit' | 'certifications' | 'assets';

interface HeaderProps {
  onToggleSidebar: () => void;
  sidebarCollapsed: boolean;
  onShowNotifications: () => void;
  currentView: ViewType;
  onViewChange: (view: ViewType) => void;
}

// =====================================================
// COMPONENTE: Selector de Framework y Dashboards
// =====================================================

interface FrameworkSelectorProps {
  currentView: ViewType;
  onViewChange: (view: ViewType) => void;
}

function FrameworkSelector({ currentView, onViewChange }: FrameworkSelectorProps) {
  const { frameworks, activeFramework, setActiveFramework } = useFramework();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Dashboards RA (Red Asistencial) disponibles - filtrados por feature flags
  const raDashboards = [
    {
      id: 'national-ra',
      name: 'Dashboard Nacional RA',
      description: 'Vista consolidada de toda la Red Asistencial Nacional',
      icon: Globe,
      color: 'purple'
    },
    {
      id: 'network-ra',
      name: 'Dashboard Red RA',
      description: 'Vista de Servicios de Salud y hospitales de la red',
      icon: Building2,
      color: 'orange'
    }
  ].filter(d => isViewEnabled(d.id));

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const getFrameworkColor = (color: string) => {
    switch (color) {
      case 'blue': return 'bg-blue-500';
      case 'indigo': return 'bg-indigo-500';
      case 'red': return 'bg-red-500';
      case 'green': return 'bg-green-500';
      case 'purple': return 'bg-purple-500';
      case 'orange': return 'bg-orange-500';
      default: return 'bg-gray-500';
    }
  };

  // Determinar el nombre a mostrar en el botón
  const getDisplayName = () => {
    if (currentView === 'national-ra') return 'Dashboard Nacional RA';
    if (currentView === 'network-ra') return 'Dashboard Red RA';
    return activeFramework?.name || 'Seleccionar Vista';
  };

  // Determinar el icono a mostrar
  const getDisplayIcon = () => {
    if (currentView === 'national-ra') return Globe;
    if (currentView === 'network-ra') return Building2;
    // Iconos específicos por framework
    if (activeFramework?.code === 'ISO27001') return ShieldCheck;
    if (activeFramework?.code === 'LMCS') return Scale;
    return Shield; // ISO27002 default
  };

  const DisplayIcon = getDisplayIcon();

  // Determinar el color del icono según el framework
  const getIconColor = () => {
    if (currentView === 'national-ra') return 'text-purple-600 dark:text-purple-400';
    if (currentView === 'network-ra') return 'text-orange-600 dark:text-orange-400';
    if (activeFramework?.code === 'ISO27001') return 'text-indigo-600 dark:text-indigo-400';
    if (activeFramework?.code === 'LMCS') return 'text-red-600 dark:text-red-400';
    return 'text-blue-600 dark:text-blue-400'; // ISO27002 default
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
      >
        <DisplayIcon className={`w-4 h-4 ${getIconColor()}`} />
        <span className="text-sm font-medium text-gray-900 dark:text-white truncate max-w-xs">
          {getDisplayName()}
        </span>
        <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-80 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50 overflow-hidden max-h-[70vh] overflow-y-auto">
          {/* Sección: Dashboards Red Asistencial (solo si hay alguno habilitado) */}
          {raDashboards.length > 0 && (
          <>
          <div className="p-2 border-b border-gray-200 dark:border-gray-700 bg-purple-50 dark:bg-purple-900/20">
            <p className="text-xs text-purple-600 dark:text-purple-400 font-medium uppercase tracking-wider">
              Dashboards Red Asistencial
            </p>
          </div>
          <div className="p-1 border-b border-gray-200 dark:border-gray-700">
            {raDashboards.map((dashboard) => {
              const Icon = dashboard.icon;
              const isActive = currentView === dashboard.id;
              return (
                <button
                  key={dashboard.id}
                  onClick={() => {
                    onViewChange(dashboard.id as ViewType);
                    setIsOpen(false);
                  }}
                  className={`w-full flex items-start gap-3 p-3 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <Icon className={`w-5 h-5 mt-0.5 ${isActive ? 'text-purple-600' : 'text-gray-500'}`} />
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {dashboard.name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      {dashboard.description}
                    </p>
                  </div>
                  {isActive && (
                    <div className="text-purple-600 dark:text-purple-400">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
          </>
          )}

          {/* Sección: Marcos Normativos */}
          <div className="p-2 border-b border-gray-200 dark:border-gray-700">
            <p className="text-xs text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wider">
              Marcos Normativos
            </p>
          </div>
          <div className="p-1">
            {frameworks.map((framework) => {
              const isActive = currentView === 'dashboard' && activeFramework?.id === framework.id;
              return (
                <button
                  key={framework.id}
                  onClick={() => {
                    setActiveFramework(framework.id);
                    onViewChange('dashboard');
                    setIsOpen(false);
                  }}
                  className={`w-full flex items-start gap-3 p-3 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <div className={`w-2 h-2 rounded-full mt-1.5 ${getFrameworkColor(framework.color)}`} />
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {framework.name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      {framework.description?.slice(0, 80)}...
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-gray-400">
                        {framework.totalControls} controles
                      </span>
                      {framework.country && (
                        <span className="text-xs px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded">
                          {framework.country}
                        </span>
                      )}
                    </div>
                  </div>
                  {isActive && (
                    <div className="text-blue-600 dark:text-blue-400">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// =====================================================
// COMPONENTE PRINCIPAL: Header
// =====================================================

export function Header({ onToggleSidebar, sidebarCollapsed, onShowNotifications, currentView, onViewChange }: HeaderProps) {
  const { user, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const { searchQuery, setSearchQuery, notifications, controls, technicalReferents, addTaskToControl, isDemoMode } = useData(); // ✅ Obtener estado de modo demo
  const { currentOrganization } = useOrganization();

  const orgName = currentOrganization?.name ?? (user?.metadata?.organizationName as string | undefined) ?? null;

  const roleLabel = {
    admin: 'Administrador',
    technical_referent: 'Referente Técnico',
    auditor: 'Auditor',
    viewer: 'Observador',
  }[user?.role ?? ''] ?? user?.role;
  const [isUserMenuOpen,    setIsUserMenuOpen]    = useState(false);
  const [isCalendarOpen,    setIsCalendarOpen]    = useState(false);
  const [pulseNotification, setPulseNotification] = useState(false);
  const [showHelp,          setShowHelp]          = useState(false);
  const [showSettings,      setShowSettings]      = useState(false);
  const [showUserMgmt,      setShowUserMgmt]      = useState(false);
  const [showGlobalBulkUpload, setShowGlobalBulkUpload] = useState(false);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);

  const unreadNotifications = notifications.filter(n => !n.read).length;

  // Trigger pulse animation when new notification arrives
  useEffect(() => {
    if (unreadNotifications > 0) {
      setPulseNotification(true);
      const timer = setTimeout(() => setPulseNotification(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [notifications.length]); // Trigger on notifications array length change

  // Keyboard shortcut: Ctrl+K to focus search
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };

    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isUserMenuOpen]);

  // Close search dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target as Node)) {
        setShowSearchDropdown(false);
      }
    };

    if (showSearchDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSearchDropdown]);

  // Show dropdown when user types (at least 2 characters)
  useEffect(() => {
    if (searchQuery && searchQuery.length >= 2) {
      setShowSearchDropdown(true);
    } else {
      setShowSearchDropdown(false);
    }
  }, [searchQuery]);

  // Handle Escape key to close dropdown
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowSearchDropdown(false);
        searchInputRef.current?.blur();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  // Listen for custom event to open Settings modal
  useEffect(() => {
    const handleOpenSettings = (e: Event) => {
      const customEvent = e as CustomEvent;
      setShowSettings(true);
      // Si el evento incluye un tab específico, podrías pasarlo al modal aquí
      console.log('[HEADER] Opening settings, tab:', customEvent.detail?.tab);
    };

    window.addEventListener('openSettings', handleOpenSettings);
    return () => window.removeEventListener('openSettings', handleOpenSettings);
  }, []);

  return (
    <>
      {/* ✅ CORRECCIÓN CRÍTICA: Banner de Modo Demo */}
      {isDemoMode && (
        <div className="bg-yellow-500 dark:bg-yellow-600 text-white px-4 py-2 text-center font-medium text-sm">
          ⚠️ MODO DEMO ACTIVO - Los datos mostrados son de ejemplo. Verifica tu conexión a internet.
        </div>
      )}

      <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          {/* Framework y Dashboard Selector */}
          <FrameworkSelector currentView={currentView} onViewChange={onViewChange} />
        </div>

      <div className="flex-1 max-w-lg mx-8">
        <div className="relative" ref={searchContainerRef}>
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Buscar controles, tareas o evidencias... (Ctrl+K)"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => searchQuery.length >= 2 && setShowSearchDropdown(true)}
            data-search
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
            aria-label="Buscar controles"
          />
          {searchQuery && (
            <button
              onClick={() => {
                setSearchQuery('');
                setShowSearchDropdown(false);
              }}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <X className="w-4 h-4" />
            </button>
          )}

          {/* Search Dropdown */}
          {showSearchDropdown && (
            <SearchDropdown
              searchQuery={searchQuery}
              controls={controls}
              onClose={() => setShowSearchDropdown(false)}
            />
          )}
        </div>
      </div>

      <div className="relative flex items-center space-x-2">
        {/* ═══════════════════════════════════════════════════════════════
         * ICONOS OCULTADOS DE LA BARRA SUPERIOR
         * ═══════════════════════════════════════════════════════════════
         * Fecha de modificación: 11 de Noviembre, 2025
         * Razón: Simplificación de la interfaz - solo mantener notificaciones visibles
         *
         * Funcionalidades aún accesibles desde:
         * - Tema (Sun/Moon): Disponible en menú de usuario
         * - Ayuda (HelpCircle): Disponible en menú de usuario
         * - Configuración (Settings): Disponible en menú de usuario
         * - Refrescar (RefreshCw): Se puede reactivar si es necesario
         *
         * Para reactivar: Descomentar los bloques correspondientes
         * ═══════════════════════════════════════════════════════════════
         */}

        {/* OCULTADO: Botón de refresh manual
        <button
          onClick={() => refreshData()}
          disabled={isLoading}
          className={`p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            isLoading ? 'opacity-50 cursor-not-allowed' : ''
          }`}
          aria-label="Refrescar datos"
          title="Refrescar datos manualmente"
        >
          <RefreshCw className={`w-5 h-5 text-gray-600 dark:text-gray-300 ${
            isLoading ? 'animate-spin' : ''
          }`} />
        </button>
        */}

        {/* OCULTADO: Toggle de tema (ahora en menú de usuario)
        <button
          onClick={toggleTheme}
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
          aria-label={isDark ? 'Cambiar a tema claro' : 'Cambiar a tema oscuro'}
        >
          {isDark ? (
            <Sun className="w-5 h-5 text-gray-600 dark:text-gray-300" />
          ) : (
            <Moon className="w-5 h-5 text-gray-600 dark:text-gray-300" />
          )}
        </button>
        */}

        {/* ✅ VISIBLE: Botón Asistente IA */}
        <AIHeaderButton />

        {/* ✅ VISIBLE: Botón de calendario global */}
        <button
          onClick={() => setIsCalendarOpen(prev => !prev)}
          className={`relative p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            isCalendarOpen ? 'bg-blue-50 dark:bg-blue-900/20' : ''
          }`}
          aria-label="Calendario de eventos"
          title="Calendario de eventos"
        >
          <CalendarDays className={`w-5 h-5 ${isCalendarOpen ? 'text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-300'}`} />
        </button>

        {/* ✅ VISIBLE: Botón de notificaciones (único icono visible en la barra) */}
        <button
          onClick={onShowNotifications}
          className={`relative p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            pulseNotification ? 'animate-pulse' : ''
          }`}
          aria-label={`Notificaciones (${unreadNotifications} sin leer)`}
        >
          <Bell className={`w-5 h-5 text-gray-600 dark:text-gray-300 ${
            pulseNotification ? 'animate-bounce' : ''
          }`} />
          {unreadNotifications > 0 && (
            <>
              {/* Badge con efecto ping cuando hay nueva notificación */}
              {pulseNotification && (
                <span className="absolute -top-1 -right-1 bg-red-500 rounded-full w-5 h-5 animate-ping"></span>
              )}
              {/* Badge con el número */}
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {unreadNotifications > 9 ? '9+' : unreadNotifications}
              </span>
            </>
          )}
        </button>

        {/* OCULTADO: Botón de ayuda (ahora en menú de usuario)
        <button
          onClick={() => setShowHelp(true)}
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
          aria-label="Ayuda"
        >
          <HelpCircle className="w-5 h-5 text-gray-600 dark:text-gray-300" />
        </button>
        */}

        {/* OCULTADO: Botón de configuración (ahora en menú de usuario)
        <button
          onClick={() => setShowSettings(true)}
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
          aria-label="Configuración"
        >
          <Settings className="w-5 h-5 text-gray-600 dark:text-gray-300" />
        </button>
        */}

          <div className="relative" ref={userMenuRef}>
            <button
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              className="flex items-center space-x-2 pl-2 border-l border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {user?.avatar_url ? (
                <img
                  src={user.avatar_url}
                  alt={user.name}
                  className="w-8 h-8 rounded-full object-cover border-2 border-blue-200 dark:border-blue-600"
                />
              ) : (
                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                  <User className="w-4 h-4 text-white" />
                </div>
              )}
              <div className="hidden md:block text-left">
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {user?.name}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                  {roleLabel}
                </p>
                {orgName && (
                  <p className="text-xs text-blue-600 dark:text-blue-400 truncate max-w-[160px]">
                    {orgName}
                  </p>
                )}
              </div>
              <ChevronDown className={`w-4 h-4 text-gray-600 dark:text-gray-300 transition-transform duration-200 ${
                isUserMenuOpen ? 'rotate-180' : ''
              }`} />
            </button>

            {isUserMenuOpen && (
              <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50">
                {/* User info header */}
                <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {user?.name}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {user?.email}
                  </p>
                  {orgName && (
                    <div className="flex items-center gap-1 mt-1">
                      <Building2 className="w-3 h-3 text-blue-500 dark:text-blue-400 shrink-0" />
                      <p className="text-xs text-blue-600 dark:text-blue-400 truncate">
                        {orgName}
                      </p>
                    </div>
                  )}
                </div>

                {/* Menu items */}
                <div className="py-1">
                  <button
                    onClick={() => {
                      setIsUserMenuOpen(false);
                      setShowSettings(true);
                    }}
                    className="w-full px-4 py-2 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
                  >
                    <Settings className="w-4 h-4" />
                    <span>Configuración</span>
                  </button>

                  {/* Botón de tema removido - el tema se configura en Configuración */}

                  {user?.role === 'admin' && (
                    <button
                      onClick={() => {
                        setIsUserMenuOpen(false);
                        setShowGlobalBulkUpload(true);
                      }}
                      className="w-full px-4 py-2 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
                    >
                      <FileSpreadsheet className="w-4 h-4" />
                      <span>Carga Masiva de Tareas</span>
                    </button>
                  )}

                  {user?.role === 'admin' && (
                    <button
                      onClick={() => {
                        setIsUserMenuOpen(false);
                        setShowUserMgmt(true);
                      }}
                      className="w-full px-4 py-2 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
                    >
                      <Users className="w-4 h-4" />
                      <span>Gestionar Usuarios</span>
                    </button>
                  )}

                  <button
                    onClick={() => {
                      setIsUserMenuOpen(false);
                      setShowHelp(true);
                    }}
                    className="w-full px-4 py-2 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
                  >
                    <HelpCircle className="w-4 h-4" />
                    <span>Ayuda</span>
                  </button>
                </div>

                {/* Separator and logout */}
                <div className="border-t border-gray-200 dark:border-gray-700 py-1">
                  <button
                    onClick={() => {
                      setIsUserMenuOpen(false);
                      logout();
                    }}
                    className="w-full px-4 py-2 text-sm text-left text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center space-x-2"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Cerrar sesión</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Help Modal */}
        {showHelp && <HelpModal onClose={() => setShowHelp(false)} />}

        {/* Settings Modal */}
        {showSettings && <SettingsModal onClose={() => setShowSettings(false)} />}

        {/* User Management Modal */}
        {showUserMgmt && <UserManagementModal onClose={() => setShowUserMgmt(false)} />}

        {/* Global Bulk Task Upload Modal */}
        {showGlobalBulkUpload && (
          <GlobalBulkTaskUploadModal
            onClose={() => setShowGlobalBulkUpload(false)}
            controls={controls}
            technicalReferents={technicalReferents}
            addTaskToControl={addTaskToControl}
          />
        )}
      </header>

      {/* Panel de Calendario (fixed, fuera del flow del header) */}
      {isCalendarOpen && (
        <CalendarPanel onClose={() => setIsCalendarOpen(false)} />
      )}
    </>
  );
}